 import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Auth/Login';
import StyleGuideLuxe from './components/Common/StyleGuideLuxe';
import ContextSelector from './components/Auth/ContextSelector';
import Layout from './components/Layout/Layout';
import Dashboard from './components/Dashboard/Dashboard';
import PilotageProduction from './components/Dashboard/PilotageProduction';
import Boutiques from './components/Admin/Boutiques';
import Users from './components/Admin/Users';
import GestionComposants from './components/Admin/GestionComposantsFabrication';
import GestionCategoriesTypes from './components/Admin/GestionCategoriesTypes';
import AdminDashboard from './components/Admin/AdminDashboard';
import ParametresBijouterie from './components/Admin/ParametresBijouterie';
import SystemeStatus from './components/Admin/SystemeStatus';
import BijouxAvance from './components/Bijoux/BijouxAvance';
import Stocks from './components/Stocks/Stocks';
import CatalogueBijoux from './components/Bijoux/CatalogueBijoux';
import ClientsAvance from './components/Clients/ClientsAvance';
import VentesAvance from './components/Ventes/VentesAvance';
import TransfertsAvance from './components/Transferts/TransfertsAvance';
import Promotions from './components/Promotions/Promotions';
import Tracabilite from './components/Tracabilite/Tracabilite';
import Fournisseurs from './components/Fournisseurs/Fournisseurs';
import Achats from './components/Achats/Achats';
import AchatsMatieres from './components/AchatsMatieres';
import PaiementsFournisseurs from './components/Paiements/PaiementsFournisseurs';
import Depenses from './components/Depenses/Depenses';
import Depots from './components/Depots/Depots';
import DepotDetail from './components/Depots/DepotDetail';
import FicheAcquisitionBijou from './components/Acquisition/FicheAcquisitionBijou';
import DevisPage from './components/Devis/DevisPage';
import Atelier from './components/Atelier/Atelier';
import OuvriersComplet from './components/Atelier/OuvriersComplet';
import HistoriqueTaches from './components/Atelier/HistoriqueTaches';
import RapportOrdresClotures from './components/Atelier/RapportOrdresClotures';
import GestionCouts from './components/GestionCouts/GestionCouts';
import SuiviFabricationAmeliore from './components/Atelier/SuiviFabricationAmeliore';
import GestionBoutique from './components/Boutique/GestionBoutique';
import VenteBoutique from './components/Vente/VenteBoutique';
import RapportFinancier from './components/Rapports/RapportFinancier';
import RapportBoutiques from './components/Rapports/RapportBoutiques';
import RapportBijoux from './components/Rapports/RapportBijoux';
import RapportOuvriers from './components/Rapports/RapportOuvriers';
import ServicesSAV from './components/Services/ServicesSAV';
import SAVListPage from './components/SAV/SAVListPage';
import Factures from './components/Factures/Factures';
import ClotureCaisse from './components/Caisse/ClotureCaisse';
import CaisseGenerale from './components/Caisse/CaisseGenerale';
import Inventaire from './components/Inventaire/Inventaire';
import AuditTrail from './components/Admin/AuditTrail';
import SystemHealth from './components/Admin/SystemHealth';
import StockComposants from './components/Stocks/StockComposants';
import BackupManager from './components/Admin/BackupManager';
import StockTracabilite from './components/Stocks/StockTracabilite';
import ImpressionEtiquettes from './components/Atelier/ImpressionEtiquettes';
import TracabiliteOr from './components/Atelier/TracabiliteOr';
import EspaceOuvrier from './components/Atelier/EspaceOuvrier';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-500">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
          <div className="text-xl text-white font-medium">Chargement...</div>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin } = useAuth();
  return user && isAdmin() ? children : <Navigate to="/" />;
};

function AppRoutes() {
  const [workspaceContext, setWorkspaceContext] = useState(() => {
    // Charger le contexte depuis localStorage dès le premier render (synchrone)
    // Évite le flash ContextSelector → Layout au rechargement de page
    try {
      // Pour les rôles fixes (non-admin), forcer le bon contexte dès le départ
      const rawUser = localStorage.getItem('user');
      const savedUser = rawUser ? JSON.parse(rawUser) : null;
      if (savedUser && savedUser.role !== 'admin') {
        if (savedUser.role === 'atelier' || savedUser.role === 'chef_atelier') {
          return { type: 'atelier', boutique_id: null, boutique_nom: 'Atelier' };
        }
        if (savedUser.role === 'ouvrier') {
          return { type: 'ouvrier', boutique_id: null, boutique_nom: 'Espace Ouvrier' };
        }
        if (savedUser.role === 'boutique') {
          return { type: 'boutique', boutique_id: savedUser.boutique_id || null, boutique_nom: savedUser.boutique_nom || 'Boutique' };
        }
      }
      const saved = localStorage.getItem('workspaceContext');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    // Si l'utilisateur a un rôle fixe (non admin), auto-sélectionner le contexte
    if (user && user.role !== 'admin') {
      const autoContext = (user.role === 'atelier' || user.role === 'chef_atelier')
        ? { type: 'atelier', boutique_id: null, boutique_nom: 'Atelier' }
        : user.role === 'boutique'
          ? { type: 'boutique', boutique_id: user.boutique_id || null, boutique_nom: user.boutique_nom || 'Boutique' }
          : user.role === 'ouvrier'
            ? { type: 'ouvrier', boutique_id: null, boutique_nom: 'Espace Ouvrier' }
            : null;
      if (autoContext) {
        localStorage.setItem('workspaceContext', JSON.stringify(autoContext));
        setWorkspaceContext(autoContext);
      }
    }
  }, [user]);

  const handleSelectContext = (context) => {
    localStorage.setItem('workspaceContext', JSON.stringify(context));
    setWorkspaceContext(context);
    navigate('/', { replace: true });
  };

  const handleChangeContext = () => {
    // Seul l'admin peut changer de contexte
    if (user && user.role !== 'admin') return;
    localStorage.removeItem('workspaceContext');
    setWorkspaceContext(null);
    navigate('/', { replace: true });
  };

  // Attendre que l'auth soit verifie avant de rendre quoi que ce soit
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-500">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
          <div className="text-xl text-white font-medium">Chargement...</div>
        </div>
      </div>
    );
  }

  // Si pas de user : afficher la page Login uniquement (bloque tout acces)
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (!workspaceContext) {
    return <ContextSelector onSelectContext={handleSelectContext} />;
  }

  return (
    <Layout workspaceContext={workspaceContext} onChangeContext={handleChangeContext}>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/style-luxe" element={<StyleGuideLuxe />} />
        <Route path="/" element={<Dashboard />} />
        <Route path="/pilotage-production" element={<PilotageProduction />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/parametres" element={<ParametresBijouterie />} />
        <Route path="/admin/systeme" element={<SystemeStatus />} />
        <Route path="/boutiques" element={<Boutiques />} />
        <Route path="/users" element={<Users />} />
        <Route path="/admin/composants" element={<GestionComposants />} />
        <Route path="/admin/categories-types" element={<GestionCategoriesTypes />} />
        <Route path="/bijoux" element={<BijouxAvance />} />
        <Route path="/catalogue-stock" element={<BijouxAvance />} /> {/* Redirige vers bijoux */}
        <Route path="/stocks" element={<Stocks />} />
        <Route path="/clients" element={<ClientsAvance />} />
        <Route path="/ventes" element={<VentesAvance />} />
        <Route path="/transferts" element={<TransfertsAvance />} />
        <Route path="/promotions" element={<Promotions />} />
        <Route path="/tracabilite" element={<Tracabilite />} />
        <Route path="/fournisseurs" element={<Fournisseurs />} />
        <Route path="/achats" element={<Achats />} />
        <Route path="/achats-matieres" element={<AchatsMatieres />} />
        <Route path="/paiements-fournisseurs" element={<PaiementsFournisseurs />} />
        <Route path="/depenses" element={<Depenses />} />
        <Route path="/depots" element={<Depots />} />
        <Route path="/depots/:id" element={<DepotDetail />} />
        <Route path="/acquisition" element={<FicheAcquisitionBijou />} />
        <Route path="/devis" element={<DevisPage />} />
        <Route path="/atelier" element={<Atelier />} />
        <Route path="/atelier/stock-composants" element={<StockComposants />} />
        <Route path="/ouvriers" element={<OuvriersComplet />} />
        <Route path="/historique-taches" element={<HistoriqueTaches />} />
        <Route path="/rapport-ordres-clotures" element={<RapportOrdresClotures />} />
        <Route path="/gestion-couts" element={<GestionCouts />} />
        <Route path="/boutique/catalogue" element={<GestionBoutique />} />
        <Route path="/vente-boutique" element={<VenteBoutique />} />
        <Route path="/rapport-financier" element={<RapportFinancier />} />
        <Route path="/rapport-boutiques" element={<RapportBoutiques />} />
        <Route path="/rapport-bijoux" element={<RapportBijoux />} />
        <Route path="/rapport-ouvriers" element={<RapportOuvriers />} />
        <Route path="/services" element={<ServicesSAV />} />
        <Route path="/sav" element={<SAVListPage />} />
        <Route path="/suivi-fabrication" element={<SuiviFabricationAmeliore />} />
        <Route path="/factures" element={<Factures />} />
        <Route path="/caisse" element={<ClotureCaisse />} />
        <Route path="/caisse-generale" element={<CaisseGenerale />} />
        <Route path="/inventaire" element={<Inventaire />} />
        <Route path="/admin/audit" element={<AuditTrail />} />
        <Route path="/admin/backup" element={<BackupManager />} />
        <Route path="/admin/health" element={<SystemHealth />} />
        <Route path="/stock-tracabilite" element={<StockTracabilite />} />
        <Route path="/impression-etiquettes" element={<ImpressionEtiquettes />} />
        <Route path="/tracabilite-or" element={<TracabiliteOr />} />
        <Route path="/mes-taches" element={<EspaceOuvrier user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
